/**
 * Choosing WHERE the lesson comes out — the speaker, not the microphone.
 *
 * WHY THIS EXISTS. The app had a microphone picker, a camera picker, a
 * microphone level meter, a microphone permission taxonomy and a microphone
 * walkthrough — and nothing whatsoever about audio OUTPUT. So "I can't hear the
 * teacher" was the one common complaint with no in-app remedy at all: the child
 * opens the only device menu they can find, changes a microphone that was
 * working fine, and is no closer.
 *
 * It is a Windows-shaped gap. On a desktop the browser plays to whatever the OS
 * says is the default output, and on the machines our classes run on that is
 * routinely not the device the person is wearing: a monitor plugged in over HDMI
 * advertises speakers and often wins the default, a USB headset can arrive as an
 * input-only default while playback stays on the laptop, and Windows keeps two
 * separate defaults ("Default" and "Communications") that disagree. Every one of
 * those is silent, instant, and completely invisible from inside the page.
 *
 * WHAT IS DELIBERATELY LIMITED. `setSinkId` is Chromium-only in practice
 * (Firefox needs media.setsinkid.enabled, which is off at our floor; Safari has
 * it nowhere), so this whole surface hides itself rather than offering a control
 * that throws. That is the honest trade: on Chrome and Edge — which is
 * essentially the entire desktop fleet — the fix is one dropdown, and everywhere
 * else nothing changes and nothing lies.
 */

import { useEffect, useRef } from 'react'
import { useRoomContext } from '@livekit/components-react'
import type { Room } from 'livekit-client'

const STORE_KEY = 'ischool-meet:speaker'

/**
 * Can this browser redirect playback at all?
 *
 * Tested on the PROTOTYPE rather than on an element we construct, so it costs
 * nothing, and checked before any UI appears: `switchActiveDevice('audiooutput')`
 * in livekit-client throws outright when setSinkId is missing, so an unguarded
 * picker would be a control whose only function is to raise an exception.
 */
export function supportsAudioOutputSelection(): boolean {
  try {
    return (
      typeof HTMLMediaElement !== 'undefined' &&
      'setSinkId' in HTMLMediaElement.prototype &&
      typeof navigator.mediaDevices?.enumerateDevices === 'function' &&
      !isSafariBased()
    )
  } catch {
    return false
  }
}

/** Keep useful labels once permissions are granted, but never hide every device. */
export function preferLabelledDevices(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
  const labelled = devices.filter((device) => device.label)
  return labelled.length > 0 ? labelled : devices
}

/**
 * Mirrors livekit-client's own `supportsSetSinkId`, which returns false for ANY
 * Safari-based browser before it even looks for the method.
 *
 * Without this the two disagree, and the disagreement is user-visible in the
 * worst way: our probe says yes, we render a speaker menu, and then
 * `switchActiveDevice('audiooutput')` throws `cannot switch audio output` — a
 * control that looks functional, is caught silently, and does nothing. A missing
 * menu is honest; a dead menu teaches people the app is broken.
 *
 * Every iOS browser is WebKit underneath, so Chrome and Edge on iPad are
 * correctly excluded here too — matching livekit rather than matching the badge
 * on the icon.
 */
function isSafariBased(): boolean {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return true
  // Every Chromium UA also contains "Safari", so the negative list is what does
  // the work; Chromium-on-iOS is already caught above.
  return /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR/.test(ua)
}

/**
 * The output devices worth showing.
 *
 * Labels are blank until some media permission has been granted, which is why
 * callers should list AFTER the microphone probe rather than before it — a menu
 * of five empty strings is worse than no menu. Devices with no label are dropped
 * for that reason, except that we never drop them all.
 */
export async function listAudioOutputs(): Promise<MediaDeviceInfo[]> {
  if (!supportsAudioOutputSelection()) return []
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const outputs = devices.filter((d) => d.kind === 'audiooutput')
    return preferLabelledDevices(outputs)
  } catch {
    return []
  }
}

/**
 * The remembered speaker, or '' for "follow the OS default".
 *
 * Persisted across lessons on purpose — unlike the microphone, which is
 * re-verified by the meter every time, a speaker choice is a stable fact about a
 * tutor's desk and making them re-pick it daily would guarantee they stop
 * bothering.
 */
export function loadSpeakerChoice(): string {
  try {
    return localStorage.getItem(STORE_KEY) ?? ''
  } catch {
    return ''
  }
}

export function saveSpeakerChoice(deviceId: string): void {
  try {
    if (deviceId) localStorage.setItem(STORE_KEY, deviceId)
    else localStorage.removeItem(STORE_KEY)
  } catch {
    /* private mode — the choice just does not outlive the tab */
  }
}

/** One output-switch path for the picker and reconnect/device-change hook. */
export async function applySpeakerChoice(room: Room, deviceId: string): Promise<boolean> {
  return room.switchActiveDevice('audiooutput', deviceId || 'default')
}

/**
 * The stored choice, but only if that device still exists.
 *
 * Load-bearing, because a persisted id is a promise about hardware. An unplugged
 * USB headset leaves a stored id that matches nothing, and handing that to
 * `setSinkId` rejects — which would route the whole lesson's audio nowhere and
 * present as "the app has no sound", the exact failure this module exists to
 * fix. A device we cannot find means fall back to the OS default, which is
 * always safe and is what the browser would have done anyway.
 */
export async function resolveSpeakerChoice(): Promise<string> {
  const stored = loadSpeakerChoice()
  if (!stored) return ''
  const outputs = await listAudioOutputs()
  return outputs.some((d) => d.deviceId === stored) ? stored : ''
}

/**
 * A short tone through a specific speaker, so "is it coming out of THIS?" is
 * answerable before the lesson rather than during it.
 *
 * Synthesised through a MediaStream rather than played from a file for one
 * concrete reason: the CSP allows `media-src 'self' blob:` and NOT `data:`, so a
 * generated data-URI clip would be blocked outright, and shipping an audio asset
 * to test a speaker would put a file on the join path for everyone.
 *
 * Quiet (0.08 gain) and short. It is a check, not an alarm, and it may well be
 * about to play into a child's headphones.
 */
export async function playTestTone(deviceId: string): Promise<void> {
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return

  const ctx = new Ctor()
  const element = document.createElement('audio')
  try {
    // Created before anything is routed so a rejected setSinkId costs us
    // nothing but a torn-down context.
    const destination = ctx.createMediaStreamDestination()
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    // A5. High enough to be audible on the tinny laptop speakers this is meant
    // to distinguish, low enough not to be unpleasant.
    oscillator.frequency.value = 880
    gain.gain.value = 0.08
    oscillator.connect(gain)
    gain.connect(destination)

    element.srcObject = destination.stream
    if (deviceId) {
      await (element as HTMLAudioElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(
        deviceId,
      )
    }
    // Autoplay policy: this is only ever called from a click, so the context is
    // already allowed to run — but resume() is free and covers the case where it
    // started suspended anyway.
    await ctx.resume().catch(() => undefined)
    await element.play()
    oscillator.start()
    await new Promise((resolve) => window.setTimeout(resolve, 600))
    oscillator.stop()
  } finally {
    // Every path closes the context. An AudioContext left open holds the output
    // device awake and browsers cap how many can exist, so leaking one per test
    // tap would eventually make the button stop working.
    element.srcObject = null
    await ctx.close().catch(() => undefined)
  }
}

/**
 * Applies the remembered speaker to the live room, and keeps applying it.
 *
 * Lives in the room rather than in the join options because the stored id must
 * be checked against real hardware first (async) and because the prejoin is not
 * the only door in — a reconnect walks straight past it, and a preference that
 * quietly stopped working after every reconnect would be worse than none.
 *
 * Re-runs on `devicechange` for the case this is really for: a tutor plugs their
 * headset in AFTER the lesson has started. That is the moment a remembered
 * choice becomes applicable again, and on Windows it is also the moment the OS
 * may move the default somewhere the tutor did not ask for.
 *
 * Every failure is swallowed to the OS default. Being unable to honour a
 * preference is a small thing; throwing inside an effect and losing the audio
 * element wiring is not.
 */
export function useSpeakerChoice(): void {
  const room = useRoomContext()
  /**
   * Have we pinned a non-default sink in this room?
   *
   * The whole reason a reset path exists. Once `switchActiveDevice` has run,
   * livekit stores the id in `room.options.audioOutput.deviceId` and calls
   * `setSinkId` with it for every participant — INCLUDING ones who join later. So
   * when the pinned headset is unplugged, simply declining to re-apply is not
   * enough: the id stays stored, every subsequent joiner's `setSinkId` rejects
   * against a device that no longer exists, and the tutor loses the audio of
   * everyone who arrived after the unplug. Which is worse than never having
   * offered the feature.
   *
   * Tracked rather than always resetting, so a participant who never chose a
   * speaker is left entirely alone and the browser's own default handling is
   * never second-guessed.
   */
  const pinned = useRef(false)

  useEffect(() => {
    if (!room || !supportsAudioOutputSelection()) return
    let cancelled = false

    const apply = async () => {
      try {
        const deviceId = await resolveSpeakerChoice()
        if (cancelled) return
        if (deviceId) {
          pinned.current = await applySpeakerChoice(room, deviceId)
          return
        }
        // No preference, or the preferred device is gone. Only act if we had
        // previously pinned one — 'default' is the real Chromium device id for
        // "whatever the OS is using", so this hands routing back to the OS
        // instead of leaving a dead id latched in the room options. Never called
        // with '', which would pin playback to a device named nothing.
        if (!pinned.current) return
        await applySpeakerChoice(room, 'default')
        pinned.current = false
      } catch {
        /* browser refused, or the device went away between listing and applying */
      }
    }

    void apply()
    const onDeviceChange = () => void apply()
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange)
    return () => {
      cancelled = true
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange)
    }
  }, [room])
}
